// src/components/layout/Footer.js
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import * as BIcon from "react-bootstrap-icons";

import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import FadeLoader from "react-spinners/FadeLoader";
import { useSnackbar } from "notistack";
import FormBotProtection from "components/common/FormBotProtection";

import {
  sendEmail,
  checkPrivateEmail,
  fadeIn,
  fadeOut,
  createAntiBotInitialValues,
  getAntiBotPayload,
} from "utils/global";

const FooterProtectionBanner = () => (
  <div
    className="footer-promo-banner"
    aria-label="urlScore.ai and SpoofGuard.io protection tools"
  >
    <div className="footer-promo-title">Two Solutions - Total Protection</div>
    <div className="footer-promo-divider"></div>
    <div className="footer-promo-shield" aria-hidden="true">
      <BIcon.CheckLg />
    </div>
    <div className="footer-promo-wave"></div>

    <div className="footer-promo-content">
      <div className="footer-promo-card footer-promo-card-urlscore">
        <div className="footer-promo-row">
          <div className="footer-promo-icon footer-promo-icon-purple">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M12 3a9 9 0 107.03 14.62l2.18 2.17 1.41-1.41-2.17-2.18A9 9 0 0012 3zm0 2a7 7 0 016.32 4h-2.2A10.8 10.8 0 0015 5.9 6.95 6.95 0 0112 5zm-2.98.9A10.8 10.8 0 007.9 9H5.68a7.05 7.05 0 013.34-3.1zM5.08 11h2.45a13.2 13.2 0 000 2H5.08a7.5 7.5 0 010-2zm.6 4h2.22c.25 1.1.63 2.14 1.12 3.1A7.05 7.05 0 015.68 15zM11 18.93A8.55 8.55 0 019.96 15H11v3.93zm0-5.93H9.55a10.53 10.53 0 010-2H11v2zm0-4H9.96A8.55 8.55 0 0111 5.07V9zm2-3.93A8.55 8.55 0 0114.04 9H13V5.07zm0 5.93h1.45a10.53 10.53 0 010 2H13v-2zm0 7.93V15h1.04A8.55 8.55 0 0113 18.93zM15 18.1c.49-.96.87-2 1.12-3.1h2.2A7.05 7.05 0 0115 18.1z"
                fill="#d172ff"
              />
            </svg>
          </div>
          <div>
            <div className="footer-promo-headline">
              url<span>Score.ai</span>
            </div>
            <div className="footer-promo-description">
              Instantly analyze any URL before visiting. Detect phishing,
              malicious domains, and suspicious behavior in real time.
            </div>
          </div>
        </div>
        <div className="footer-promo-button-row">
          <a
            className="footer-promo-button footer-promo-button-purple"
            href="https://urlscore.ai"
            target="_blank"
            rel="noreferrer"
          >
            URLSCORE.AI
          </a>
        </div>
      </div>

      <div className="footer-promo-card footer-promo-card-spoof">
        <div className="footer-promo-row">
          <div className="footer-promo-icon footer-promo-icon-blue">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M12 2l7 3v6c0 5-3.4 9.7-7 11-3.6-1.3-7-6-7-11V5l7-3zm0 3.1L7 7v4c0 3.8 2.4 7.5 5 8.7 2.6-1.2 5-4.9 5-8.7V7l-5-1.9z"
                fill="#52a4ff"
              />
              <path
                d="M9.5 11h5v5h-5v-5zm1-1.1a1.5 1.5 0 013 0V11h1.5V9.9a3 3 0 00-6 0V11h1.5V9.9z"
                fill="#52a4ff"
              />
            </svg>
          </div>
          <div>
            <div className="footer-promo-headline">
              Spoof<span>Guard.io</span>
            </div>
            <div className="footer-promo-description">
              Prevent phishing, impersonation and malicious lookalike domains.
            </div>
          </div>
        </div>
        <div className="footer-promo-button-row">
          <a
            className="footer-promo-button footer-promo-button-blue"
            href="https://spoofguard.io"
            target="_blank"
            rel="noreferrer"
          >
            SPOOFGUARD.IO
          </a>
        </div>
      </div>
    </div>
  </div>
);

const Footer = () => {
  const routesForNewBox = ["/pricing"];

  const location = useLocation();
  const isNewBox = routesForNewBox.includes(location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      const toTop = document.querySelector("#return-to-top");

      if (window.scrollY > 250) {
        if (toTop.style.opacity === "" || parseFloat(toTop.style.opacity) <= 0)
          fadeIn(toTop, 200);
      } else {
        if (parseFloat(toTop.style.opacity) >= 1) fadeOut(toTop, 200);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <footer className="container-fluid">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-12">
          <div className="col-span-12 boxHolder">
            <div className="footer-top-stack">
              <FooterProtectionBanner />
              {!isNewBox ? (
                <div className="box mb-10">
                  <div className="">
                    <h3 className="text-center mb-4">
                      Request a Live Demo or <br />
                      Book Directly Here
                      <a
                        href="/calendly"
                        target="_blank"
                        className="ms-3 inline-block"
                      >
                        <div className="flex items-center">
                          <BIcon.Calendar2Date className="me-2 !text-black" />{" "}
                          Calendly
                        </div>
                      </a>
                    </h3>
                  </div>
                  <EmailForm />
                </div>
              ) : (
                <div className="c-flex neflex pricing">
                  <div className="box newbox">
                    <div className="d-flex">
                      <h3>
                        Request a Live Demo or <br />
                        Book Directly Here
                        <a href="/calendly" target="_blank">
                          <i className="bi bi-calendar-date"></i> Calendly
                        </a>
                      </h3>
                    </div>
                    <EmailForm />
                  </div>
                  <div className="box newbox sbox">
                    <div className="d-flex">
                      <h3>Team Up with Us</h3>
                    </div>
                    <div className="d-flex">
                      <a href="/partners">
                        <button type="button">Become a partner</button>
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <a title="" href="/">
              <img
                title=""
                src={require("../../assets/images/flogo.png")}
                className="img-fluid logo"
                alt=""
              />
            </a>
            <p className="mt-2 md:mt-4">
              <a href="/dark-web-monitoring" className="primary-color">
                Dark Web Monitoring
              </a>
              <span> Threat Intelligence</span>
            </p>
            <div className="links">
              <a title="" href="/privacy-policy">
                Privacy
              </a>
              &nbsp;&nbsp;|&nbsp;&nbsp;
              <a title="" href="/imprint">
                Imprint
              </a>
              &nbsp;&nbsp;|&nbsp;&nbsp;
              <a title="" href="/tos">
                ToS
              </a>
               &nbsp;&nbsp;|&nbsp;&nbsp;
              <a title="" href="/cyber-laws">
                Laws
              </a>
               &nbsp;&nbsp;|&nbsp;&nbsp;
              <a title="" href="/sitemap">
                Sitemap
              </a>
              {/* &nbsp;&nbsp;|&nbsp;&nbsp;
              <a title="" href="https://ch.trustpilot.com/review/kaduu.io">
                Rate us Trustpilot
              </a> */}
            </div>
            <div class="footer-info">
              <div class="footer-email">
                <BIcon.Envelope size={18} color="white" />
                <a class="primary-color" href="mailto:info@darknetsearch.com">
                  info@darknetsearch.com
                </a>
              </div>
              <div class="footer-address">
                <BIcon.GeoAlt size={18} color="white" />
                <span>Switzerland</span>
              </div>
              <div className="footer-cta-row">
                <div className="global-btn footer">
                  <a href="/free-trial">
                    Start your free trial now
                  </a>
                </div>
                <div className="global-btn footer">
                  <a href="/darknet-report-24h">
                    Request a Darknet Report
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 fborder">
            <h5>Navigation</h5>
            <ul>
              <li>
                <a title="" href="/">
                  Home
                </a>
              </li>
              <li>
                <a title="" href="/about-us">
                  About Us
                </a>
              </li>
              <li>
                <a title="" href="/pricing">
                  Pricing
                </a>
              </li>
              <li>
                <a title="" href="/use-cases">
                  Use Cases
                </a>
              </li>
              <li>
                <a title="" href="/contact">
                  Contact
                </a>
              </li>
              <li>
                <a title="" href="/partners">
                  Partner with us
                </a>
              </li>
              <li>
                <a title="" target="_blank" href="https://apps.kaduu.io/">
                  Kaduu Ecosystem
                </a>
              </li>
              <li>
                <a title="" href="/what-is-dark-web">
                  What Is the Dark Web?
                </a>
              </li>
              <li>
                <a title="" href="/documentation">
                  Documentation
                </a>
              </li>
            </ul>
          </div>
          <div className="lg:col-span-3 fborder">
            <h5>Resources</h5>
            <ul>
              <li>
                <a title="" href="/knowledge">
                  News
                </a>
              </li>
              <li>
                <a title="" href="/frequently-asked-questions">
                  FAQs
                </a>
              </li>
              <li>
                <a title="" href="https://wiki.kaduu.ch/doku/doku.php">
                  Wiki
                </a>
              </li>
              <li>
                <a target="_blank" title="" href="/docs/darknetsearch.pdf">
                  Whitepaper
                </a>
              </li>
              <li>
                <a title="" href="/glossary">
                  Glossary
                </a>
              </li>
              <li>
                <a title="" href="/calendly">
                  Schedule a demo
                </a>
              </li>
              <li>
                <a title="" href="/customer-success-stories">
                  Customer Success Stories
                </a>
              </li>
              <li>
                <a title="" href="/compare">
                  Compare Darknetsearch
                </a>
              </li>
              <li>
                <a title="" href="/reports">
                  Dark Web Reports
                </a>
              </li>
            </ul>
          </div>
          <div className="lg:col-span-2 fborder">
            <h5>Languages</h5>
            <ul>
              <li>
                <a title="Spain" href="/es">
                  Español
                </a>
              </li>
              <li>
                <a title="France" href="/fr">
                  Français
                </a>
              </li>
              <li>
                <a title="Germany" href="/de">
                  Deutsch
                </a>
              </li>
              {/* <li>
                <a title="" href="#">
                  US/CA
                </a>
              </li> */}
            </ul>
          </div>
          <div className="lg:col-span-1 sfborder">
            <div className="flex flex-col justify-center d-flex justify-content-lg-center">
              {/* <a
                title=""
                href="https://www.instagram.com/kaduu.io"
                target="_blank"
              >
                <BIcon.Instagram />
              </a> */}
              <a
                title=""
                href="https://www.youtube.com/@kaduudarkwebmonitoring"
                target="_blank"
              >
                <BIcon.Youtube />
              </a>
              <a
                title="DarknetSearch on X"
                href="https://x.com/darknetsearch"
                target="_blank"
                rel="noreferrer"
                aria-label="DarknetSearch on X"
              >
                <BIcon.TwitterX />
              </a>
              <a
                title="Bluesky"
                href="https://bsky.app/profile/darknetsearch.bsky.social"
                target="_blank"
              >
                <svg
                  className="bluesky-icon"
                  viewBox="0 0 600 530"
                  width="22"
                  height="22"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M300 245.3c-36-71.5-134-181.7-229.3-248.9C-20.7-68.4-63.3-41.6-43.5 52.5c16.6 79 71.6 163 125.4 224.2-95.2-14.7-186.7-6.6-70.3 100.9 118.1 108.8 276.5-59.6 288.4-77.6 11.9 18 170.3 186.4 288.4 77.6 116.4-107.5 24.9-115.6-70.3-100.9 53.8-61.2 108.8-145.2 125.4-224.2 19.8-94.1-22.8-120.9-114.2-56.1C434 63.6 336 173.8 300 245.3z"
                  />
                </svg>
              </a>
              <a
                title=""
                href="https://www.linkedin.com/company/kaduu/"
                target="_blank"
              >
                <BIcon.Linkedin />
              </a>
              <a
                title=""
                href="mailto:info@kaduu.ch?cc=alejandra@kaduu.io,mayra@kaduu.io"
              >
                <BIcon.EnvelopeFill />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="copy">
        <p>
          Copyright &copy; <span id="year">{new Date().getFullYear()}</span>{" "}
          Kaduu. All Right Reserved "Dark Web Monitoring"
        </p>
      </div>
      <div className="top">
        <a
          id="return-to-top"
          title="Return to Top"
          className="inline"
          onClick={handleToTop}
        >
          <i>
            <BIcon.ArrowUp />
          </i>
        </a>
      </div>
    </footer>
  );
};

const EmailForm = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);

  return (
    <Formik
      initialValues={{
        name: "",
        email: "",
        companyName: "",
        referralSource: "",
        ...createAntiBotInitialValues(),
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string()
          .min(2, "Invalid name!")
          .max(50, "Invalid name!")
          .required("Name is required"),
        email: Yup.string()
          .email("Invalid email address!")
          .required("Business email is required")
          .test(
            "check-private-email",
            "Only business email required",
            checkPrivateEmail
          ),
        companyName: Yup.string()
          .min(2, "Invalid organisation name!")
          .max(80, "Organisation name is too long")
          .required("Organisation name is required"),
        referralSource: Yup.string().required("Please select an option"),
      })}
      onSubmit={async (values, { resetForm }) => {
        setLoading(true);

        const result = await sendEmail(
          values.email,
          {
            subject: "Live demo request",
            message: `Title: Live Demo request\r\n\r\nUser Name: ${values.name}\r\nEmail: ${values.email}\r\nCompany: ${values.companyName}\r\nHow did you find us?: ${values.referralSource}`,
          },
          enqueueSnackbar,
          {
            type: "live_demo_request",
            name: values.name,
            company: values.companyName,
          },
          getAntiBotPayload(values)
        );

        if (result) resetForm();
        setLoading(false);
      }}
    >
      {({ errors, touched }) => (
        <Form className="d-flex">
          <FormBotProtection />
          <div className="w-full me-2 mb-4">
            <Field
              name="name"
              type="text"
              placeholder="Name"
              disabled={loading}
              className="w-full"
            />
            {errors.name && touched.name ? (
              <div className="formik-error">{errors.name}</div>
            ) : null}
          </div>

          <div className="w-full me-2 mb-4">
            <Field
              name="email"
              type="email"
              placeholder="Business Email"
              disabled={loading}
              className="w-full"
            />
            {errors.email && touched.email ? (
              <div className="formik-error">{errors.email}</div>
            ) : null}
          </div>
          <div className="w-full me-2 mb-4">
            <Field
              name="companyName"
              type="text"
              placeholder="Organisation Name"
              disabled={loading}
              className="w-full"
            />
            {errors.companyName && touched.companyName ? (
              <div className="formik-error">{errors.companyName}</div>
            ) : null}
          </div>
          <div className="w-full me-2 mb-4">
            <Field
              as="select"
              name="referralSource"
              placeholder="How did you find us?"
              disabled={loading}
              className="w-full  me-2"
            >
              <option value={""} className="first">
                How did you find us?
              </option>
              <option value="Google Search">Google Search</option>
              <option value="AI tools (ChatGPT, Perplexity, etc.)">
                AI tools (ChatGPT, Perplexity, etc.)
              </option>
              <option value="Gartner Reviews">Gartner Reviews</option>
              <option value="LinkedIn">LinkedIn</option>
              <option value="X (Twitter)">X (Twitter)</option>
              <option value="Referral / recommendation">
                Referral / recommendation
              </option>
              <option value="Partner">Partner</option>
              <option value="Other">Other</option>
            </Field>
            {errors.referralSource && touched.referralSource ? (
              <div className="formik-error">{errors.referralSource}</div>
            ) : null}
          </div>
          <div className="w-full">
            <button
              type="submit"
              className={loading ? "loading" : "w-full"}
              disabled={loading}
            >
              <div className="flex items-center justify-center">
                <span>Live Demo</span>
                <div
                  className="formik-loading ms-6 me-[-12px]"
                  style={{ display: loading ? "inline" : "none" }}
                >
                  <FadeLoader
                    height={8}
                    margin={-8}
                    radius={3}
                    width={3}
                    cssOverride={{ top: 19 }}
                  />
                </div>
              </div>
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default Footer;
